import { Flex, Text } from '@chakra-ui/react';
import { faHourglassHalf } from '@fortawesome/pro-light-svg-icons';
import { Accordion, Badge, ButtonIcon } from '@morressier/alembic';
import { getReviewHistoryAccordionSubTitle } from 'src/components/Submission/ReviewTab/ReviewRoundHistory/ReviewsHistory';
import { useReviewData } from 'src/contexts/ReviewDataProvider';
import EmptyState from 'src/components/Submission/BoardMemberReviewsTab/EmptyState';
import { IGetRevisionHistory } from '@publishing-engine/submission-service/src/endpoints/api.response-types';
import { PastReview } from 'src/components/Submission/PastReview/PastReviews';
import { Tooltip } from '@chakra-ui/tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import { formatDate } from 'src/utils/date';
import dateFormat from 'dateformat';
export type IPastRevisionHistory = {
  id: string;
  journalName: string;
  rounds: IGetRevisionHistory[];
};

export type IPastRevisionHistoryResponse = {
  previousReviews: IPastRevisionHistory[];
};
const getReviewHistoryAccordionTitle = (
  revision: IGetRevisionHistory,
  round: number
) => {
  const date = `${dateFormat(
    revision?.dateEntered!,
    'mmmm d, yyyy'
  )} to ${dateFormat(revision?.dateCompleted!, 'mmmm d, yyyy')}`;
  return round === 0
    ? `Original Submission (${date})`
    : `Revision -  ${round} (${date})`;
};
const PastReviewsTab = ({ submissionContextId }: { submissionContextId?: string }) => {
  const { pastReviewsData } = useReviewData();

  const renderRevisionRoundGroup = (
    revisionGroup: {
      journalName: string;
      rounds: IGetRevisionHistory[];
      dateEntered?: string;
    },
    isCurrent: boolean
  ) => {
    const { journalName, rounds, dateEntered } = revisionGroup;

    return (
      <Flex direction="column" gap={3} my={3}>
        <Flex flexDirection="column" gap={3} mb={3}>
          <Flex justifyContent="space-between">
            <Text textStyle="textBody" fontWeight="semibold">
              {journalName} Feedback
            </Text>
            <Badge variant={isCurrent ? 'attention' : 'neutral'} size="context">
              {isCurrent ? 'Current Journal' : 'Transferred'}
            </Badge>
          </Flex>
          {!isCurrent && (
            <Text textStyle="textBody">
              Transferred to {journalName} on{' '}
              {formatDate(new Date(dateEntered ?? ''))}
            </Text>
          )}
        </Flex>
        {rounds.length > 0 && (
          <Accordion
            data-testId="revisions-history-overview"
            chakraAccordionProps={{
              allowMultiple: true,
              defaultIndex: [],
              style: {
                display: 'flex',
                flexDirection: 'column',
                gap: 3
              }
            }}
            accordionItemProps={{
              background: 'neutralPrimaryContentInverse',
              marginBottom: 0
            }}
            items={rounds?.map((revision, index) => {
              return {
                title: (
                  <Text textStyle={'headingItem'}>
                    {revision?.reviewerRevisionFeedback &&
                      (revision?.reviewerRevisionFeedback[0]?.reviewAssessment
                        ?.submissionManuscriptId || revision.manuscriptId) +
                        ' | ' +
                        getReviewHistoryAccordionTitle(
                          revision,
                          Number(
                            revision?.reviewerRevisionFeedback[0]
                              ?.reviewAssessment?.submissionRoundNumber ||
                              revision.submissionRoundNumber
                          ) || 0
                        )}
                  </Text>
                ),
                subtitle: getReviewHistoryAccordionSubTitle(
                  rounds,
                  revision,
                  index
                ),
                content: <PastReview round={revision} submissionContextId={submissionContextId} />
              };
            })}
          />
        )}
      </Flex>
    );
  };

  return (
    <Flex direction="column">
      <Text textStyle="headingTitle">
        Past Reviews{' '}
        <Tooltip
          hasArrow
          shouldWrapChildren
          placement="top"
          label={'Access previous versions of reviews for this submission.'}
          borderRadius={4}
        >
          <ButtonIcon
            size="small"
            ml={1}
            ariaLabel="Help Icon"
            icon={<FontAwesomeIcon icon={faInfoCircle} />}
          />
        </Tooltip>
      </Text>

      {pastReviewsData?.previousReviews?.length > 0 ? (
        pastReviewsData?.previousReviews?.map((review, index: number) =>
          renderRevisionRoundGroup(review, index === 0 ? true : false)
        )
      ) : (
        <EmptyState
          title="No Past Review History Yet"
          subTitle="Review history will be recorded once a starting peer review round is closed."
          icon={faHourglassHalf}
        />
      )}
    </Flex>
  );
};

export default PastReviewsTab;
